import {foldDiacritics, isStandaloneFunctionWord, normalizeKeywordPhrase} from '@/service/keywordStemDensity';

/**
 * How many words of the current main keyword we index when deciding
 * whether a one-word row is just a split of that phrase.
 * One forward pass, then stop. No replace loop.
 */
export const PHRASE_SPLIT_TOKEN_CAP = 32;

/**
 * True when `phrase` must not be its own keyword:
 * a Czech/English function word, or one word taken from the current main phrase.
 * The full main phrase stays. A longer suggestion that only contains one of
 * those words (for example "investování pro začátečníky") stays.
 */
export function isPromotedPhraseFragment(phrase: unknown, mainKeyword = ''): boolean {
    const key = normalizeKeywordPhrase(String(phrase ?? ''));
    const mainKey = normalizeKeywordPhrase(String(mainKeyword ?? ''));
    if (!key || key === mainKey) {
        return false;
    }
    if (key.includes(' ')) {
        return false;
    }
    if (isStandaloneFunctionWord(key)) {
        return true;
    }
    if (!mainKey.includes(' ')) {
        return false;
    }
    const parts = mainKey.split(' ');
    const cap = parts.length < PHRASE_SPLIT_TOKEN_CAP ? parts.length : PHRASE_SPLIT_TOKEN_CAP;
    const foldedKey = foldDiacritics(key);
    for (let index = 0; index < cap; index++) {
        const part = parts[index];
        if (part === key || foldDiacritics(part) === foldedKey) {
            return true;
        }
    }
    return false;
}

/** One forward pass. Drops fragment rows. Does not rewrite phrase text. */
export function omitPromotedPhraseFragments<T>(
    rows: readonly T[] | null | undefined,
    mainKeyword: string,
    textOf: (row: T) => unknown,
): T[] {
    const source = rows || [];
    const kept: T[] = [];
    for (let index = 0; index < source.length; index++) {
        if (!isPromotedPhraseFragment(textOf(source[index]), mainKeyword)) {
            kept.push(source[index]);
        }
    }
    return kept;
}
